import React, { useState } from 'react';
import { Modal, Button, Table, Header, Loader, Message, Icon, Label } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { AvailableSlotDTO, AvailableSlotsRequestDTO, TimeSlot } from '../../../app/models/AvailableGameSlots';
import { useStore } from '../../../app/stores/store';
// Import your translation function - adjust the import path as needed
import { useTranslation } from 'react-i18next'; // or your preferred i18n library

interface Props {
    open: boolean;
    onClose: () => void;
    onSlotSelect?: (slot: AvailableSlotDTO, timeSlot: TimeSlot) => void;
    requestData: AvailableSlotsRequestDTO | null;
}

export default observer(function AvailableSlotsModal({ open, onClose, onSlotSelect, requestData }: Props) {
    const { t } = useTranslation(); // or your translation hook
    const { gameStore } = useStore();
    const { availableSlots, loadingAvailableSlots, findAvailableSlots } = gameStore;
    const [selectedSlot, setSelectedSlot] = useState<{ slot: AvailableSlotDTO; timeSlot: TimeSlot } | null>(null);

    React.useEffect(() => {
        if (open && requestData) {
            findAvailableSlots(requestData);
        }
    }, [open, requestData, findAvailableSlots]);

    const handleSlotSelect = (slot: AvailableSlotDTO, timeSlot: TimeSlot) => {
        setSelectedSlot({ slot, timeSlot });
        if (onSlotSelect) {
            onSlotSelect(slot, timeSlot);
        }
    };

    const renderTimeSlots = (slots: TimeSlot[], venue: AvailableSlotDTO) => {
        return slots.map((timeSlot, index) => (
            <Label
                key={index}
                as="a"
                color="blue"
                style={{ margin: '2px', cursor: 'pointer' }}
                onClick={() => handleSlotSelect(venue, timeSlot)}
            >
                {timeSlot.startTime} - {timeSlot.endTime}
            </Label>
        ));
    };

    return (
        <Modal open={open} onClose={onClose} size="large">
            <Modal.Header>
                <Icon name="calendar alternate outline" />
                {t('availableSlots.modal.title')}
                {availableSlots && (
                    <Label color="teal" style={{ marginLeft: '10px' }}>
                        {availableSlots.searchDate} • {availableSlots.searchRadius}{t('availableSlots.modal.radiusLabel')}
                    </Label>
                )}
            </Modal.Header>
            
            <Modal.Content scrolling>
                {loadingAvailableSlots && (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Loader active inline="centered" content={t('availableSlots.modal.loading')} />
                    </div>
                )}

                {!loadingAvailableSlots && availableSlots && availableSlots.availableSlots.length === 0 && (
                    <Message info>
                        <Message.Header>{t('availableSlots.modal.noSlotsFound.title')}</Message.Header>
                        <p>
                            {t('availableSlots.modal.noSlotsFound.message', { radius: availableSlots.searchRadius })}
                        </p>
                    </Message>
                )}

                {!loadingAvailableSlots && availableSlots && availableSlots.availableSlots.length > 0 && (
                    <>
                        <Header as="h4" style={{ marginBottom: '15px' }}>
                            {t('availableSlots.modal.foundVenues', { count: availableSlots.availableSlots.length })}
                        </Header>
                        
                        <Table celled>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>{t('availableSlots.modal.table.headers.venueDetails')}</Table.HeaderCell>
                                    <Table.HeaderCell>{t('availableSlots.modal.table.headers.distance')}</Table.HeaderCell>
                                    <Table.HeaderCell>{t('availableSlots.modal.table.headers.availableTimes')}</Table.HeaderCell>
                                    <Table.HeaderCell>{t('availableSlots.modal.table.headers.pricePerHour')}</Table.HeaderCell>
                                    <Table.HeaderCell>{t('availableSlots.modal.table.headers.contact')}</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {availableSlots.availableSlots.map((venue, index) => (
                                    <Table.Row key={index}>
                                        <Table.Cell>
                                            <div>
                                                <Header as="h5" style={{ margin: '0 0 5px 0' }}>
                                                    {venue.companyName}
                                                </Header>
                                                <div style={{ fontSize: '0.9em', color: '#666' }}>
                                                    <strong>{venue.venueName}</strong> • {venue.assetName}
                                                </div>
                                            </div>
                                        </Table.Cell>
                                        
                                        <Table.Cell>
                                            <Label size="small" color="grey">
                                                {venue.distanceKm} {t('availableSlots.modal.table.distanceUnit')}
                                            </Label>
                                        </Table.Cell>                                                                                                               
                                        <Table.Cell>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                {renderTimeSlots(venue.availableSlots, venue)}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            {venue.defaultPricePerHour ? (
                                                <Label color="green">
                                                    ${venue.defaultPricePerHour}{t('availableSlots.modal.table.priceLabel')}
                                                </Label>
                                            ) : (
                                                <Label basic>
                                                    {t('availableSlots.modal.table.contactVenue')}
                                                </Label>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {venue.venueContactPhone && (
                                                <div>
                                                    <Icon name="phone" />
                                                    {venue.venueContactPhone}
                                                </div>
                                            )}
                                        </Table.Cell>     
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </>
                )}

                {selectedSlot && (
                    <Message positive>
                        <Message.Header>{t('availableSlots.modal.selectedSlot.title')}</Message.Header>
                        <p>
                            <strong>{selectedSlot.slot.companyName}</strong> - {selectedSlot.slot.venueName} ({selectedSlot.slot.assetName})
                            <br />
                            {t('availableSlots.modal.selectedSlot.time')} {selectedSlot.timeSlot.startTime} - {selectedSlot.timeSlot.endTime}
                            <br />
                            {t('availableSlots.modal.selectedSlot.distance')} {selectedSlot.slot.distanceKm} {t('availableSlots.modal.table.distanceUnit')}
                        </p>
                    </Message>
                )}
            </Modal.Content>

            <Modal.Actions>
                <Button onClick={onClose}>
                    {t('availableSlots.modal.buttons.close')}
                </Button>
                {selectedSlot && (
                    <Button color="green" onClick={onClose}>
                        <Icon name="checkmark" />
                        {t('availableSlots.modal.buttons.useSlot')}
                    </Button>
                )}
            </Modal.Actions>
        </Modal>
    );
});